import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "../../layouts/header";
import golf from "../../assets/images/2025-Program/golf-ball-flag.jpg";
import { useTranslation } from "react-i18next";
import { FaDownload } from "react-icons/fa";
import axios from "axios";
import { useSelector } from "react-redux";

export default function InboundTrips() {
  const { t } = useTranslation();
  const location = useLocation();
  const baseUrl = useSelector((state) => state.gtm.baseUrl);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInboundTrips = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${baseUrl}/trips/inbound`);
        
        if (response.data.success && response.data.data) {
          setTrips(response.data.data);
        } else {
          setError("Failed to load trips");
        }
      } catch (err) {
        console.error("Error fetching inbound trips:", err);
        setError("Failed to load trips. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchInboundTrips();
  }, [baseUrl]);

  useEffect(() => {
    if (location.hash && trips.length > 0) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location, trips]);

  // Generate slug from title for section IDs
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  // Extract filename from URL for download
  const getFilenameFromUrl = (url) => {
    const urlParts = url.split("/");
    return urlParts[urlParts.length - 1] || "trip.pdf";
  };

  if (loading) {
    return (
      <div>
        <Header image={golf} title="Inbound Trips" />
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Header image={golf} title="Inbound Trips" />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header image={golf} title="Inbound Trips" />
      <div className="flex justify-center items-center">
        <div className="h-full flex flex-col justify-start items-center gap-4 md:gap-4 w-full md:w-[75%] px-4 md:px-8 mb-10 md:mb-20">
          <div className="flex flex-col gap-4 md:gap-8 w-full">
            <div className="w-full">
              <div className="flex justify-center items-center mb-4"></div>
              {trips.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-gray-600">{t("trips.no_trips") || "No inbound trips available."}</p>
                </div>
              ) : (
                <div className="space-y-6 md:space-y-8">
                  {trips.map((trip) => {
                    const sectionId = trip.slug || generateSlug(trip.title);
                    return (
                      <section key={trip.id} id={sectionId} className="scroll-mt-20">
                        <h2 className="text-lg md:text-2xl font-bold mb-3 md:mb-4 px-2 md:px-0">
                          {trip.title}
                        </h2>
                        <div className="bg-gray-50 p-4 md:p-6 rounded-lg">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0 mb-4">
                            <p className="text-sm md:text-base text-gray-600">
                              {t("trips.pdf_message")}
                            </p>
                            <a
                              href={trip.pdf_url}
                              download={getFilenameFromUrl(trip.pdf_url)}
                              className="flex items-center justify-center gap-2 px-4 py-2 bg-[#00573f] text-white rounded-lg hover:opacity-80 transition-opacity text-sm md:text-base whitespace-nowrap"
                            >
                              <FaDownload />
                              <span>{t("trips.download") || "Download"}</span>
                            </a>
                          </div>
                          <div className="w-full h-[400px] md:h-[600px] rounded-lg overflow-hidden border border-gray-300">
                            <iframe
                              src={trip.pdf_url}
                              className="w-full h-full"
                              title={trip.title}
                            />
                          </div>
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

